import React, { useEffect, useState, useCallback } from "react";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { makeStyles, withStyles } from "@material-ui/core/styles";

import Autocomplete from "@material-ui/lab/Autocomplete";

import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ClearIcon from "@material-ui/icons/Clear";

import { findlot } from "../../../adminModel/lots";
import { AuctionList } from "../../../adminModel/auctions";


// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(3),
    backgroundColor: "#fff",
  },

  filterSection: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    backgroundColor: "#fafafa",
    border: "1px solid #e6e6e6",
    borderRadius: 8,
  },

  filterField: {
    backgroundColor: "#fff",

    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
    },
  },

  filterButton: {
    height: 40,
    borderRadius: 6,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  container: {
    maxHeight: 640,
    border: "1px solid #e5e5e5",
    borderRadius: 8,
  },

  table: {
    minWidth: 900,
  },

  lotLink: {
    cursor: "pointer",
    color: "#1976d2",
    fontWeight: 500,

    "&:hover": {
      textDecoration: "underline",
    },
  },

  lotNumber: {
    fontWeight: 700,
    marginRight: 4,
  },

  statusSold: {
    display: "inline-flex",
    alignItems: "center",
    color: "#2e7d32",
  },

  statusUnsold: {
    display: "inline-flex",
    alignItems: "center",
    color: "#d32f2f",
  },

  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: "#777",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));


// =====================================================
// TABLE CELL
// =====================================================

const StyledTableCell = withStyles(() => ({
  root: {
    padding: "11px 14px",
    fontSize: 13,
    color: "#333",
    borderBottom: "1px solid #e5e5e5",
    verticalAlign: "middle",
  },

  head: {
    backgroundColor: "#f5f5f5",
    color: "#222",
    fontSize: 12,
    fontWeight: 700,
    padding: "12px 14px",
    borderBottom: "2px solid #ddd",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
}))(TableCell);


// =====================================================
// COMPONENT
// =====================================================

export default function CustomizedTables() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [eventSearchText, setEventSearchText] = useState("");

  const [geteventList, setEventList] = useState([]);

  const [getData, setGetdata] = useState([]);

  const [count, setCount] = useState(0);

  const [disable, setDisable] = useState(false);

  const [auctionID, setAuctionID] = useState("");

  const [setID, setSetID] = useState("");

  const [eventName, setEventName] = useState("");


  // =====================================================
  // FETCH AUCTION / EVENT LIST
  // =====================================================

  const fetchEvents = useCallback(async (query = "") => {
    try {
      setLoading(true);

      const results = await AuctionList(query, 0);

      if (results && results.data) {
        setEventList(results.data);
      } else {
        setEventList([]);
      }
    } catch (error) {
      console.error("Fetch Events Error:", error);
      setEventList([]);
    } finally {
      setLoading(false);
    }
  }, []);


  // =====================================================
  // INITIAL EVENT LOAD
  // =====================================================

  useEffect(() => {
    fetchEvents("");
  }, [fetchEvents]);


  // =====================================================
  // FIND LOTS
  // =====================================================

  const FetchAuction = useCallback(
    async (searchValue = "", auctionId = "") => {
      try {
        setLoading(true);

        const results = await findlot(
          searchValue,
          auctionId
        );

        if (!results || results === false) {
          setDisable(false);
          setGetdata([]);
          setCount(0);
          return;
        }

        setDisable(true);

        if (results.data && results.data.length > 0) {
          setSetID(results.data[0].set_id);
        } else {
          setSetID("");
        }

        setGetdata(results.data || []);
        setCount(results.count || 0);

      } catch (error) {
        console.error("FetchAuction error:", error);

        setDisable(false);
        setGetdata([]);
        setEventList([]);
        setCount(0);

      } finally {
        setLoading(false);
      }
    },
    []
  );


  // =====================================================
  // SEARCH
  // =====================================================

  const handleClickSearch = () => {
    FetchAuction(search, auctionID);
  };


  // =====================================================
  // SELECT AUCTION
  // =====================================================

  const handlepick = (event, value) => {
    try {
      if (!value) {
        setAuctionID("");
        setEventName("");
        setSetID("");
        return;
      }

      setAuctionID(value.id);

      localStorage.setItem(
        "auctionName",
        value.auction_name
      );

      setEventName(value.auction_name);

    } catch (error) {
      console.error("Error in handlepick:", error);

      setAuctionID("");
      setEventName("");
      setSetID("");
    }
  };


  // =====================================================
  // VIEW LOT DETAILS
  // =====================================================

  const handleLotDetails = (
    event,
    auctionId,
    lotNumber,
    lotSetID,
    title
  ) => {
    if (!auctionId || !lotNumber) {
      alert("Both Event and Search are required.");
      return;
    }

    window.location.href =
      `/app/lot/explore/${encodeURIComponent(
        title
      )}/${lotNumber}/${auctionId}/${lotNumber}/${lotSetID}`;
  };


  // =====================================================
  // LOT STATUS
  // =====================================================

  const renderStatus = (status) => {
    switch (status) {
      case "SOLD":
        return (
          <span className={classes.statusSold}>
            <CheckBoxIcon fontSize="small" />
          </span>
        );

      case "RESERVE_NOT_MET":
      case "UNSOLD":
      case "PASS":
        return (
          <span className={classes.statusUnsold}>
            <ClearIcon fontSize="small" />
          </span>
        );

      case "PENDING":
      default:
        return null;
    }
  };


  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Paper
      className={classes.root}
      elevation={0}
    >

      {/* =================================================
          FILTER
      ================================================= */}

      <Box className={classes.filterSection}>

        <Grid
          container
          spacing={2}
          alignItems="center"
        >

          {/* Auction / Event */}

          <Grid item md={5} xs={12}>

            <Autocomplete
              disablePortal
              fullWidth
              options={geteventList}
              value={
                geteventList.find(
                  (item) => item.id === auctionID
                ) || null
              }
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option?.auction_name || ""
              }
              onInputChange={(
                event,
                newInputValue
              ) => {
                setEventSearchText(
                  newInputValue
                );

                fetchEvents(
                  newInputValue
                );
              }}
              onChange={handlepick}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Auction"
                  placeholder="Select auction"
                  variant="outlined"
                  size="small"
                  className={
                    classes.filterField
                  }
                  InputProps={{
                    ...params.InputProps,

                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress
                            size={18}
                          />
                        )}

                        {
                          params.InputProps
                            .endAdornment
                        }
                      </>
                    ),
                  }}
                />
              )}
            />

          </Grid>


          {/* Lot Search */}

          <Grid item md={5} xs={12}>

            <TextField
              fullWidth
              label="Search Lot"
              placeholder="Enter lot number or keyword"
              variant="outlined"
              size="small"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              className={
                classes.filterField
              }
            />

          </Grid>


          {/* Filter Button */}

          <Grid item md={2} xs={12}>

            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={handleClickSearch}
              disabled={
                loading ||
                !auctionID ||
                !search
              }
              className={
                classes.filterButton
              }
            >
              Search
            </Button>

          </Grid>

        </Grid>

      </Box>


      {/* =================================================
          RESULTS
      ================================================= */}

      <TableContainer
        className={classes.container}
      >

        <Table
          stickyHeader
          className={classes.table}
          aria-label="auction lots table"
        >

          <TableHead>

            <TableRow>

              <StyledTableCell align="left">
                Lot Name
              </StyledTableCell>

              <StyledTableCell align="left">
                Artist
              </StyledTableCell>

              <StyledTableCell align="right">
                SP
              </StyledTableCell>

              <StyledTableCell align="right">
                HP
              </StyledTableCell>

              <StyledTableCell align="center">
                Status
              </StyledTableCell>

              <StyledTableCell align="left">
                Winner
              </StyledTableCell>

            </TableRow>

          </TableHead>


          <TableBody>

            {getData.length > 0 ? (

              [...getData]
                .sort(
                  (a, b) =>
                    a.lot_number -
                    b.lot_number
                )
                .map((row) => (

                  <TableRow
                    hover
                    key={row.id}
                  >

                    {/* Lot */}

                    <StyledTableCell
                      align="left"
                      onClick={(event) =>
                        handleLotDetails(
                          event,
                          row.auction_id,
                          row.lot_number,
                          row.set_id,
                          row.title
                        )
                      }
                      className={
                        classes.lotLink
                      }
                    >

                      <span
                        className={
                          classes.lotNumber
                        }
                      >
                        {row.lot_number}
                      </span>

                      {row.title}

                    </StyledTableCell>


                    {/* Artist */}

                    <StyledTableCell align="left">
                      {row.artist || "-"}
                    </StyledTableCell>


                    {/* Starting Price */}

                    <StyledTableCell align="right">
                      {Number(
                        row.starting_price
                      )
                        ? Number(
                            row.starting_price
                          ).toLocaleString()
                        : "0"}
                    </StyledTableCell>


                    {/* Hammer Price */}

                    <StyledTableCell align="right">
                      {Number(row.hp)
                        ? Number(
                            row.hp
                          ).toLocaleString()
                        : "0"}
                    </StyledTableCell>


                    {/* Status */}

                    <StyledTableCell align="center">
                      {renderStatus(
                        row.lot_status
                      )}
                    </StyledTableCell>


                    {/* Winner */}

                    <StyledTableCell align="left">
                      {[
                        "RESERVE_NOT_MET",
                        "UNSOLD",
                        "PASS",
                      ].includes(
                        row.lot_status
                      )
                        ? ""
                        : row.email || "-"}
                    </StyledTableCell>

                  </TableRow>

                ))

            ) : (

              <TableRow>

                <StyledTableCell
                  colSpan={6}
                  align="center"
                >

                  <Box
                    className={
                      classes.emptyState
                    }
                  >

                    <Typography
                      variant="body2"
                      color="textSecondary"
                    >
                      No lots found.
                    </Typography>

                  </Box>

                </StyledTableCell>

              </TableRow>

            )}

          </TableBody>

        </Table>

      </TableContainer>


      {/* =================================================
          LOADING
      ================================================= */}

      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </Paper>
  );
}